import { useId, type InputHTMLAttributes, type ReactNode, type Ref, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { cn } from '@/lib/utils';

const controlBase =
  'w-full rounded-[6px] border bg-white px-3.5 text-[15px] text-ink placeholder:text-muted/80 transition-colors focus:outline-none focus:ring-2 focus:ring-accent/25 disabled:bg-surface disabled:text-muted';
const controlState = (invalid: boolean) => (invalid ? 'border-red-400 focus:border-red-500' : 'border-line focus:border-accent');

interface FieldShellProps {
  id: string;
  label: ReactNode;
  required?: boolean;
  error?: string;
  hint?: ReactNode;
  className?: string;
  children: ReactNode;
}

function FieldShell({ id, label, required, error, hint, className, children }: FieldShellProps) {
  return (
    <div className={className}>
      <label htmlFor={id} className="mb-1.5 block text-[12px] font-semibold text-ink-soft">
        {label}
        {required && (
          <span className="text-red-600" aria-hidden="true">
            {' '}*
          </span>
        )}
      </label>
      {children}
      {hint && !error && (
        <p id={`${id}-hint`} className="mt-1 text-[11.5px] text-muted">
          {hint}
        </p>
      )}
      {error && (
        <p id={`${id}-error`} className="mt-1 text-[12px] font-medium text-red-700">
          {error}
        </p>
      )}
    </div>
  );
}

function describedBy(id: string, error?: string, hint?: ReactNode) {
  return error ? `${id}-error` : hint ? `${id}-hint` : undefined;
}

type Common = { label: ReactNode; error?: string; hint?: ReactNode; wrapperClassName?: string };

export function TextField({ label, error, hint, wrapperClassName, className, id: idProp, ref, ...rest }: Common & InputHTMLAttributes<HTMLInputElement> & { ref?: Ref<HTMLInputElement> }) {
  const generated = useId();
  const id = idProp ?? generated;
  return (
    <FieldShell id={id} label={label} required={rest.required} error={error} hint={hint} className={wrapperClassName}>
      <input
        id={id}
        ref={ref}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(id, error, hint)}
        className={cn(controlBase, 'h-11', controlState(Boolean(error)), className)}
        {...rest}
      />
    </FieldShell>
  );
}

export function TextAreaField({ label, error, hint, wrapperClassName, className, id: idProp, ref, ...rest }: Common & TextareaHTMLAttributes<HTMLTextAreaElement> & { ref?: Ref<HTMLTextAreaElement> }) {
  const generated = useId();
  const id = idProp ?? generated;
  return (
    <FieldShell id={id} label={label} required={rest.required} error={error} hint={hint} className={wrapperClassName}>
      <textarea
        id={id}
        ref={ref}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(id, error, hint)}
        className={cn(controlBase, 'min-h-[110px] py-2.5 leading-relaxed', controlState(Boolean(error)), className)}
        {...rest}
      />
    </FieldShell>
  );
}

export function SelectField({ label, error, hint, wrapperClassName, className, id: idProp, children, ref, ...rest }: Common & SelectHTMLAttributes<HTMLSelectElement> & { ref?: Ref<HTMLSelectElement> }) {
  const generated = useId();
  const id = idProp ?? generated;
  return (
    <FieldShell id={id} label={label} required={rest.required} error={error} hint={hint} className={wrapperClassName}>
      <select
        id={id}
        ref={ref}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(id, error, hint)}
        className={cn(controlBase, 'h-11 pr-8', controlState(Boolean(error)), className)}
        {...rest}
      >
        {children}
      </select>
    </FieldShell>
  );
}

export function CheckboxField({ label, error, className, id: idProp, ref, ...rest }: { label: ReactNode; error?: string } & InputHTMLAttributes<HTMLInputElement> & { ref?: Ref<HTMLInputElement> }) {
  const generated = useId();
  const id = idProp ?? generated;
  return (
    <div className={className}>
      <div className="flex items-start gap-2.5">
        <input
          id={id}
          ref={ref}
          type="checkbox"
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${id}-error` : undefined}
          className="mt-0.5 h-4 w-4 shrink-0 rounded border-line-strong accent-[var(--color-accent)]"
          {...rest}
        />
        <label htmlFor={id} className="text-[12.5px] leading-snug text-ink-soft">
          {label}
        </label>
      </div>
      {error && (
        <p id={`${id}-error`} className="mt-1 text-[12px] font-medium text-red-700">
          {error}
        </p>
      )}
    </div>
  );
}
